import { Box, Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import ModalN010 from 'practice/components/shared/n010-modal';
import { ID_STATES_PRACTICE } from 'practice/constants/id-states-practice';
import {
  PracticeManagementStep,
  TypeInitiative,
} from 'practice/constants/practice-management-step';
import { TypeRegister } from 'practice/constants/type-register';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { Fragment, memo, useMemo, useState } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { noop } from 'shell/utils/noop';

interface ButtonsProps {
  handleExit: () => void;
  disabled?: boolean;
  printVerbalOfPraticeUnavailabilty: () => void;
  handleStep?: (id: number) => void;
  activeStep?: number;
  redirectUriHistory?: boolean;
  isDetail?: boolean;
  onPrintFolder: () => void;
  onPrintReceipt: () => void;
  viewMessi?: string;
  printAttachmentDeclarationDeclarant: () => void;
  printDeclarationOfPraticeunavailabilty: () => void;
  printComunicationProcedureStart: () => void;
  printComunicationToPerson: () => void;
  printComunicationToPersonBO: () => void;
  hasAttentionNote?: boolean;
  tipoIniziativa?: string;
  typeRegister?: string;
}

const PraticeUnAvailabilityButtonsComponent = ({
  handleExit,
  handleStep = noop,
  activeStep,
  redirectUriHistory,
  isDetail = false,
  onPrintFolder,
  printVerbalOfPraticeUnavailabilty,
  printComunicationProcedureStart,
  printComunicationToPerson,
  printComunicationToPersonBO,
  printDeclarationOfPraticeunavailabilty,
  viewMessi,
  onPrintReceipt,
  hasAttentionNote,
  tipoIniziativa,
  typeRegister,
}: ButtonsProps): React.ReactElement => {
  const formContext = useFormikContext<PracticeDetailVM>();

  const isCloseNegativePositive = useMemo(() => {
    return (
      formContext.values.practice?.codiceStatoPratica === ID_STATES_PRACTICE.POSITIVO ||
      formContext.values.practice?.codiceStatoPratica === ID_STATES_PRACTICE.NEGATIVO
    );
  }, [formContext.values.practice?.codiceStatoPratica]);

  const disabledUlterioriAccertamenti = useMemo(() => {
    if (
      isDetail ||
      hasAttentionNote ||
      isCloseNegativePositive ||
      redirectUriHistory ||
      typeRegister === TypeRegister.RegisterOfficeTemporary
    ) {
      return true;
    }

    return viewMessi !== '3';
  }, [isDetail, isCloseNegativePositive, formContext.values.practice, hasAttentionNote]);

  const disabledStampaComunicazioniOstative = useMemo(() => {
    if (
      isDetail ||
      isCloseNegativePositive ||
      typeRegister === TypeRegister.RegisterOfficeTemporary
    ) {
      return true;
    }

    return !(
      formContext.values.practice?.tipoOperazione === '06' ||
      formContext.values.practice?.tipoOperazione === '10'
    );
  }, [
    isDetail,
    isCloseNegativePositive,
    typeRegister,
    formContext.values.practice?.tipoOperazione,
  ]);

  const disabledSospensionePratica = useMemo(() => {
    if (isDetail || isCloseNegativePositive || typeRegister === TypeRegister.DeleteAire) {
      return true;
    }

    return formContext.values.practice?.codiceStatoPratica === '5';
  }, [
    isDetail,
    isCloseNegativePositive,
    typeRegister,
    formContext.values.practice?.codiceStatoPratica,
  ]);

  const steps: Array<{
    label: string;
    disabled: boolean;
    paddingTop?: string;
    index?: PracticeManagementStep;
  }> = useMemo(() => {
    // Disabilita step registrazione ritorno accertamento messi
    let disabledRegistraRitornoAccertamentoMessi =
      isDetail || isCloseNegativePositive ? true : viewMessi !== '1';

    // disabilita step ritorno accertamento messi
    let disabledRitornoAccertamentoMessi =
      isDetail || isCloseNegativePositive ? true : viewMessi !== '2';

    // disabilita annullo accertamento
    let disabledAnnulloAccertamento =
      isDetail || isCloseNegativePositive
        ? true
        : !(formContext.values.practice?.tipoOperazione === '06');

    // disabilita ulteriore accertamento
    let disabledRichiestaUlterioreAccertamento = disabledUlterioriAccertamenti;
    // disabilita richiesta ulteriore accertamento messi
    let disabledRichiestaUlterioreAccertamentoMessi =
      isDetail || isCloseNegativePositive ? true : viewMessi !== '4';

    // disabilita registrazione sospensione pratica
    let disabledRegistraSospensionePratica = disabledSospensionePratica;
    // disabilita fine sospensione pratica
    let disabledRegistraFineSospensionePratica =
      isDetail || isCloseNegativePositive
        ? true
        : !(
            formContext.values.practice?.codiceStatoPratica === '5' &&
            formContext.values.practice?.tipoOperazione === '50'
          );

    // disabilita stampa ostativa
    let disabledStampaComunicazioneOstativa = disabledStampaComunicazioniOstative;

    // disablita ritorno comunicazione ostativa
    const disabledRitornoComunicazioneOstativa = true;

    // disabilita richiesta accertamento
    let disabledRichiestaAccertamento = false;

    // disabilita se ultima operazione, tipo iniziativa sportello
    if (
      formContext.values.practice?.tipoOperazione === '18' &&
      (formContext.values.practice?.tipoIniziativa === '1' ||
        formContext.values.practice?.tipoIniziativa === '2')
    ) {
      disabledRegistraRitornoAccertamentoMessi = true;
      disabledRitornoAccertamentoMessi = true;
      disabledAnnulloAccertamento = true;
      disabledRichiestaUlterioreAccertamento = false;
      disabledRichiestaUlterioreAccertamentoMessi = true;
      disabledStampaComunicazioneOstativa = false;
      disabledRegistraFineSospensionePratica = true;
    }

    // disabilita se ultima operazione, tipo iniziativa sportello
    if (
      formContext.values.practice?.tipoOperazione === '01' &&
      formContext.values.practice?.tipoIniziativa === '2'
    ) {
      disabledRichiestaUlterioreAccertamento = false;
    }

    // disabilita se ultima operazione, tipo iniziativa ufficio - sportello
    if (
      formContext.values.practice?.tipoOperazione === '18' &&
      formContext.values.practice?.tipoIniziativa === '2'
    ) {
      disabledRegistraRitornoAccertamentoMessi = true;
      disabledRitornoAccertamentoMessi = true;
      disabledAnnulloAccertamento = true;
      disabledRichiestaUlterioreAccertamento = true;
      disabledRichiestaUlterioreAccertamentoMessi = false;
      disabledRegistraSospensionePratica = false;
      disabledStampaComunicazioneOstativa = false;
      disabledRegistraFineSospensionePratica = true;
    }

    // Se tipo operazione 13, dopo annullo accertamento
    // abilta registrazione richiesta ulteriore accertamento
    if (formContext.values.practice?.tipoOperazione === '13') {
      disabledRichiestaUlterioreAccertamento = false;
    }

    if (formContext.values.practice?.operations?.find((o) => o.tipoOperazione === '05')) {
      disabledRichiestaAccertamento = true;
    }

    return [
      {
        label: 'Registra ritorno accertamento messi',
        disabled: disabledRegistraRitornoAccertamentoMessi,
        index: PracticeManagementStep.RegistraRitornoAccertamentoMessi,
      },
      {
        label: 'Richiesta accertamento',
        disabled: disabledRichiestaAccertamento || typeRegister === TypeRegister.UntraceableCensus,
        index: PracticeManagementStep.RichiestaAccertamento,
      },
      {
        label: 'Ritorno accertamento messi',
        disabled: disabledRitornoAccertamentoMessi,
        index: PracticeManagementStep.RitornoAccertamentoMessi,
      },
      {
        label: 'Annullo Accertamento',
        disabled: disabledAnnulloAccertamento,
        index: PracticeManagementStep.AnnulloAccertamento,
      },
      {
        label: 'Richiesta ulteriori accertamenti',
        disabled: disabledRichiestaUlterioreAccertamento,
        index: PracticeManagementStep.RichiestaUlterioriAccertamenti,
      },
      {
        label: 'Richiesta ulteriore accertamento messi',
        disabled: disabledRichiestaUlterioreAccertamentoMessi,
        index: PracticeManagementStep.RichiestaUlterioreAccertamentoMessi,
      },
      {
        label: 'Registra sospensione pratica',
        disabled: disabledRegistraSospensionePratica,
        index: PracticeManagementStep.RegistraSospensionePratica,
      },
      {
        label: 'Registra fine sospensione pratica',
        disabled: disabledRegistraFineSospensionePratica,
        index: PracticeManagementStep.RegistraFineSospensionePratica,
      },
      {
        label: 'Stampa comunicazioni ostative (art. 10 bis)',
        disabled:
          disabledStampaComunicazioneOstativa || typeRegister === TypeRegister.UntraceableCensus,
        index: PracticeManagementStep.StampaComunicazioneOstativa,
      },
      {
        label: 'Ritorno comunicazioni ostative (art. 10 bis)',
        disabled: disabledRitornoComunicazioneOstativa,
        index: PracticeManagementStep.RitornoComunicazioneOstativa,
      },
      {
        label: 'Rettifica indirizzo',
        disabled: true,
        paddingTop: '1.5rem',
        index: PracticeManagementStep.RettificaIndirizzo,
      },
      {
        label: 'Contatti',
        disabled: redirectUriHistory ? true : false,
        index: PracticeManagementStep.Contatti,
      },
      {
        label: 'Allegati',
        disabled: redirectUriHistory ? true : false,
        index: PracticeManagementStep.Allegati,
      },
    ];
  }, [
    redirectUriHistory,
    isCloseNegativePositive,
    disabledSospensionePratica,
    disabledUlterioriAccertamenti,
    disabledStampaComunicazioniOstative,
    formContext.values.practice,
    isDetail,
    viewMessi,
    typeRegister,
  ]);

  const [openN010Modal, setOpenN010Modal] = useState(false);

  return (
    <div className="pmi-position-sticky pmi-top-0">
      <Grid container spacing={1}>
        {steps.map((step, index) => (
          <Grid
            item
            xs={12}
            key={step.label?.toString()}
            paddingTop={
              step.paddingTop?.toString() ? `${step.paddingTop?.toString()} !important` : '0'
            }
          >
            <StepButton
              active={(step.index || index) === activeStep}
              disabled={Boolean(step.disabled)}
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              onClick={() => handleStep(step.index || index)}
            >
              <StepTypography
                active={(step.index || index) === activeStep}
                disabled={Boolean(step.disabled)}
              >
                {step.label}
              </StepTypography>
            </StepButton>
          </Grid>
        ))}
      </Grid>
      <div className="pmi-pt-4">
        {typeRegister === TypeRegister.Unavailable ||
        typeRegister === TypeRegister.UntraceableCensus ? (
          <Fragment>
            <div className="pmi-pb-2">
              <StepButton
                isButton
                variant="outlined"
                color="cdmBrandThird"
                onClick={() => printComunicationProcedureStart()}
                fullWidth
                type="button"
              >
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Stampa comunicazione avvio procedimento
                </Box>
              </StepButton>
            </div>
          </Fragment>
        ) : undefined}
        {tipoIniziativa === '1' && typeRegister === TypeRegister.Unavailable ? (
          <Fragment>
            <div className="pmi-pb-2">
              <StepButton
                isButton
                variant="outlined"
                color="cdmBrandThird"
                onClick={() => printComunicationToPerson()}
                fullWidth
                type="button"
              >
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Stampa comunicazione a interessato
                </Box>
              </StepButton>
            </div>
          </Fragment>
        ) : undefined}
        {tipoIniziativa === TypeInitiative.idUfficio &&
        typeRegister === TypeRegister.Unavailable ? (
          <Fragment>
            <div className="pmi-pb-2">
              <StepButton
                isButton
                variant="outlined"
                color="cdmBrandThird"
                onClick={() => printComunicationToPersonBO()}
                fullWidth
                type="button"
              >
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Stampa comunicazione a interessato
                </Box>
              </StepButton>
            </div>
          </Fragment>
        ) : undefined}
        <div className="pmi-pb-2">
          <StepButton
            isButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => printDeclarationOfPraticeunavailabilty()}
            fullWidth
            type="button"
            disabled={
              tipoIniziativa === TypeInitiative.idUfficio ||
              typeRegister === TypeRegister.UntraceableCensus
            }
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              Ristampa dichiarazione allo sportello
            </Box>
          </StepButton>
        </div>
        <div className="pmi-pb-2">
          <StepButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => onPrintReceipt()}
            fullWidth
            type="button"
            disabled={
              tipoIniziativa === TypeInitiative.idUfficio ||
              typeRegister === TypeRegister.DeleteAire ||
              typeRegister === TypeRegister.UntraceableCensus
            }
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              Stampa Ricevuta
            </Box>
          </StepButton>
        </div>
        <div className="pmi-pb-2">
          <StepButton
            isButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => onPrintFolder()}
            fullWidth
            type="button"
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              Stampa Cartellina
            </Box>
          </StepButton>
        </div>
        {formContext.values.practice?.codiceStatoPratica === ID_STATES_PRACTICE.NEGATIVO &&
        formContext.values.practice?.tipoOperazione === '90' ? (
          <div className="pmi-pb-2">
            <PmiButton
              variant="outlined"
              color="cdmBrandThird"
              onClick={() => setOpenN010Modal(true)}
              fullWidth
              type="button"
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                Nota di ripristino
              </Box>
            </PmiButton>
            <ModalN010
              open={openN010Modal}
              isIrreper
              onClose={() => setOpenN010Modal(false)}
              onSave={() => {
                //Chiamata a servizio e poi chiusura modale
                setOpenN010Modal(false);
              }}
            />
          </div>
        ) : undefined}
        <div className="pmi-pb-2">
          <StepButton
            isButton
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={() => printVerbalOfPraticeUnavailabilty()}
            disabled={
              typeRegister === TypeRegister.Unavailable ||
              typeRegister === TypeRegister.UntraceableCensus
                ? formContext.values.practice?.codiceStatoPratica !== '8'
                : false
            }
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              Stampa verbale di irreperibilità
            </Box>
          </StepButton>
        </div>

        <div className="pmi-pt-4">
          <div className="pmi-pb-2">
            <StepButton
              variant="outlined"
              color="cdmBrandThird"
              active={PracticeManagementStep.ChiusuraPositiva === activeStep}
              fullWidth
              onClick={() => handleStep(PracticeManagementStep.ChiusuraPositiva)}
              disabled={isDetail || isCloseNegativePositive}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                chiusura per cancellazione anagrafica
              </Box>
            </StepButton>
          </div>
          <div className="pmi-pb-2">
            <StepButton
              active={PracticeManagementStep.ChiusuraNegativa === activeStep}
              variant="outlined"
              color="cdmBrandThird"
              onClick={() => handleStep(PracticeManagementStep.ChiusuraNegativa)}
              fullWidth
              type="button"
              disabled={
                isDetail || isCloseNegativePositive || typeRegister === TypeRegister.DeleteAire
              }
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                Chiusura negativa
              </Box>
            </StepButton>
          </div>
        </div>
        <div className="pmi-pt-3">
          <PmiButton variant="outlined" color="cdmBrand" fullWidth onClick={handleExit}>
            Esci
          </PmiButton>
        </div>
      </div>
    </div>
  );
};

PraticeUnAvailabilityButtonsComponent.displayName = 'Reprint CID Page - Buttons';

const ButtonsIsUnAvailability = memo(PraticeUnAvailabilityButtonsComponent);

export default ButtonsIsUnAvailability;
